import React, { useEffect, useMemo, useRef } from 'react';
import { Select, SelectItem, Selection } from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { useTranslation } from '../../hooks/useTranslation';

interface BusinessTypeFilterProps {
  data: Settlement[];
  selectedBusinessType: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectAll?: boolean;
}

const MISSING_BUSINESS_TYPE = 'Sin Tipo de Negocio';
const LEGACY_MISSING_BUSINESS_TYPE_LABELS = new Set([
  MISSING_BUSINESS_TYPE,
  'No Business Type',
]);

const getBusinessTypeKey = (type?: string | null) => {
  const trimmed = type?.trim();
  return trimmed || MISSING_BUSINESS_TYPE;
};

const BusinessTypeFilter: React.FC<BusinessTypeFilterProps> = ({
  data,
  selectedBusinessType,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectAll = false,
}) => {
  const { t } = useTranslation('common');
  const finalLabel = label || t('Tipo de Negocio');
  const defaultSelectionApplied = useRef(false);

  const displayBusinessType = (type: string) =>
    type === MISSING_BUSINESS_TYPE ? t(MISSING_BUSINESS_TYPE) : type;

  const businessTypes = useMemo(() => {
    const typeCount = data.reduce(
      (acc, item) => {
        const type = getBusinessTypeKey(item.business_type);
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    return Object.entries(typeCount)
      .sort(([, countA], [, countB]) => countB - countA)
      .map(([type]) => type);
  }, [data]);

  const selectedBusinessTypes = useMemo(() => {
    const values = Array.isArray(selectedBusinessType)
      ? selectedBusinessType
      : [selectedBusinessType];

    return values
      .filter(Boolean)
      .map((type) =>
        LEGACY_MISSING_BUSINESS_TYPE_LABELS.has(type)
          ? MISSING_BUSINESS_TYPE
          : type
      );
  }, [selectedBusinessType]);

  useEffect(() => {
    if (
      selectionMode === 'multiple' &&
      defaultSelectAll &&
      !defaultSelectionApplied.current &&
      selectedBusinessTypes.length === 0 &&
      businessTypes.length > 0
    ) {
      defaultSelectionApplied.current = true;
      onSelectionChange(businessTypes);
    }
  }, [
    businessTypes,
    defaultSelectAll,
    onSelectionChange,
    selectedBusinessTypes.length,
    selectionMode,
  ]);

  if (businessTypes.length === 0) return null;

  const handleSelectionChange = (keys: Selection) => {
    if (selectionMode === 'multiple') {
      const keysArray = Array.from(keys);
      if (keysArray.includes('all')) {
        if (selectedBusinessTypes.length > 0) {
          onSelectionChange([]);
        } else {
          onSelectionChange(businessTypes);
        }
      } else {
        onSelectionChange(keysArray as string[]);
      }
    } else {
      onSelectionChange(Array.from(keys) as string[]);
    }
  };

  return (
    <Select
      label={finalLabel}
      selectedKeys={
        selectionMode === 'multiple'
          ? new Set(selectedBusinessTypes)
          : new Set(selectedBusinessTypes)
      }
      onSelectionChange={handleSelectionChange}
      selectionMode={selectionMode}
      className={className}
      size={size}
      aria-label={t('Selecciona tipo de negocio')}
      renderValue={(items) => {
        if (
          Array.isArray(selectedBusinessType) &&
          selectedBusinessTypes.length === businessTypes.length &&
          selectedBusinessTypes.length > 0
        ) {
          return (
            <div className='flex items-center gap-2'>
              {t('Todos los tipos de negocio')}
            </div>
          );
        }
        if (selectedBusinessTypes.length > 0) {
          const displayCount = 2;
          const first = selectedBusinessTypes
            .slice(0, displayCount)
            .map(displayBusinessType);
          const remaining = selectedBusinessTypes.length - displayCount;
          return (
            <div className='flex items-center gap-2'>
              {first.join(', ')}
              {remaining > 0 && ` +${remaining}`}
            </div>
          );
        }
        return items;
      }}
    >
      {selectionMode === 'multiple' && (
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          <div className='flex justify-between items-center'>
            <span>
              {selectedBusinessTypes.length > 0
                ? t('Deseleccionar todos')
                : t('Seleccionar todos')}
            </span>
          </div>
        </SelectItem>
      )}
      {businessTypes.map((type) => (
        <SelectItem key={type} value={type} textValue={type}>
          {displayBusinessType(type)}
        </SelectItem>
      ))}
    </Select>
  );
};

export default React.memo(BusinessTypeFilter);
